import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { OnboardingActions } from './index';
import { catchError, debounceTime, exhaustMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';
import { ApiService, USER_ROLES } from '../../services/api.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { OnboardingState, selectGeneralInfo, selectJobTitles, selectStepsState, selectTalentGeneral, selectTalentHardSkills, selectTalentSoftSkills } from './onboarding.store';
import { selectOnboardingUserRole } from '../user/user.store';
import { HmGeneralAdapter, PartnerGeneralAdapter, TalentGeneralAdapter, TalentSkillsAdapter } from './onboarding.adapter';
import { UserProxy } from '../user/user.proxy';
import { ONBOARDING_TALENT_STEPS } from './onboarding.proxy';
import { StepItems } from 'src/app/shared/components/stepper/stepper.component';
import { StepValid } from './onboarding.actions';
import { SetUserName } from '../user/user.actions';

@Injectable()
export class OnboardingEffects {

  checkServerSteps$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.CheckServerSteps),
    withLatestFrom(this.store.select(selectOnboardingUserRole)),
    tap(([ action, role ]) => {
      if (role) {
        this.apiService.getOnboardingSteps().subscribe(res => {
          if (res && !res.error) {
            this.store.dispatch(OnboardingActions.SetSteps({ steps: res.stepItems, currentStep: res.currentStep }));
            if (res.currentRole) { this.userProxy.setRole(res.currentRole) }
            const route = (res.currentStep === 0 || !res.stepItems) ? 'account-type' : res.stepItems[res.currentStep].path;
            console.log("RESPONSE", route, res)
            this.router.navigate(['onboarding/' + route]);
          } else {
            this.router.navigate(['onboarding/account-type']);
          }
        }, err => {
          this.router.navigate(['onboarding/account-type']);
        })
      } else {
        this.router.navigate(['onboarding/account-type']);
      }
    })
  ), { dispatch: false });

  initSteps$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.InitSteps),
    withLatestFrom(this.store.select(selectOnboardingUserRole)),
    tap(([ action, role ]) => {
      return this.store.dispatch(OnboardingActions.SetSteps({ steps: this.setStepsByRole(role), currentStep: 1 }))
    })
  ), { dispatch: false });

  setCurrentSteps$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.SetCurrentStep),
    withLatestFrom(this.store.select(selectStepsState)),
    tap(([ action, steps ]) => {
      return this.store.dispatch(OnboardingActions.SetSteps({ steps, currentStep: action.currentStep }))
    })
  ), { dispatch: false });

  setSteps$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.SetSteps),
    tap((action) => {
      return this.apiService.setOnboardingSteps(action.steps, action.currentStep).subscribe();
    })
  ), { dispatch: false });

  setGeneralInfo$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.SetGeneralInfo),
    concatLatestFrom(() => this.store.select(selectTalentGeneral)),
    tap(async ([action, talentGeneral]) => {
      let data;
      if (action && action.section === USER_ROLES.talent) {
        data = TalentGeneralAdapter.toServer({ ...(talentGeneral || {}), ...action.data });
      }
      if (action && action.section === USER_ROLES.partner) {
        data = PartnerGeneralAdapter.toServer(action.data);
      }
      if (action && action.section === USER_ROLES.hm) {
        if (!action.data.company.id) {
          const company = {
            name: action.data.company.name,
            location: { country: action.data.company.country }
          }
          const companyId = await this.apiService.createCompany(company).toPromise();
          if (!companyId) {
            alert('Company creation failed, please contact support.')
            return;
          } else {
            data = HmGeneralAdapter.toServer({ ...action.data, company: { id: companyId  }}); 
          }
        } else {
          data = HmGeneralAdapter.toServer(action.data);
        }
      }
      if (data) {
        this.apiService.updateUserGeneralInfo(action.section, data).subscribe(() => {
          this.store.dispatch(StepValid({ 
            section: action.section, 
            key: ONBOARDING_TALENT_STEPS.general, 
            data: action.data 
          }));
          this.store.dispatch(SetUserName({ name: data.name }));
        });
      }
    })
  ), { dispatch: false });

  getGeneralInfo$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.GetGeneralInfo),
    withLatestFrom(this.store.select(selectOnboardingUserRole)),
    concatLatestFrom(([ action, role ]) => this.store.select(selectGeneralInfo(role))), 
    switchMap(([[ action, role ], userInfo]) => {
      if (!userInfo && role) {
        return this.apiService.getUserGeneralInfo(role).pipe(
          map(res => {
            let payload = null;
            if (role === USER_ROLES.talent) {
              payload = TalentGeneralAdapter.toForm(res);
            }
            if (role === USER_ROLES.hm) {
              payload = HmGeneralAdapter.toForm(res);
            }
            if (role === USER_ROLES.partner) {
              payload = PartnerGeneralAdapter.toForm(res);
            }
            return OnboardingActions.GetGeneralInfoSuccess({ role, payload });
          }),
          catchError((err) => of(OnboardingActions.GetGeneralInfoFailure({ err })))
        )
      }
      return of();
    })
  ));

  getJobTitles$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.GetJobTitles),
    withLatestFrom(this.store.select(selectJobTitles)),
    switchMap(([ action, jobTitles ]) => {
      if (!jobTitles) {
        return this.apiService.getJobTitles().pipe(
          map(jobTitles => OnboardingActions.GetJobTitlesSuccess({ jobTitles })),
          catchError((err) => of(OnboardingActions.GetGeneralInfoFailure({ err })))
        )
      }
      return of();
    })
  ));

  getSkillsList$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.GetSkillsList),
    exhaustMap(() => {
      return combineLatest([
        this.apiService.getUserSkillTree(), 
        this.apiService.getUserHardSkills()
      ]).pipe(
        map(([ skillTree, skills ]) => {
          return OnboardingActions.SetHardSkills({ 
            skillsList: TalentSkillsAdapter.toViewMerge(skillTree.branches, skills),
            noServer: true
          });
        }),
        catchError((err) => of(OnboardingActions.SetHardSkills({ skillsList: [] })))
      );
    })
  ));

  getHardSkills$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.GetHardSkills),
    withLatestFrom(this.store.select(selectTalentHardSkills)),
    switchMap(([ action, skills ]) => {
      if (!skills) {
        return this.apiService.getUserHardSkills().pipe(
          map(skillsList => OnboardingActions.GetHardSkillsSuccess({ skillsList })),
          catchError((err) => of(OnboardingActions.GetHardSkillsFailure({ err })))
        )
      }
      return of();
    })
  ));

  setHardSkills$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.SetHardSkills),
    debounceTime(1000),
    tap((action) => {
      const skills = TalentSkillsAdapter.toServer(action.skillsList);
      if (skills && !action.noServer) {
        this.apiService.setUserHardSkills(skills).subscribe();
      }
    })
  ), { dispatch: false });

  getSoftSkills$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.GetSoftSkills),
    withLatestFrom(this.store.select(selectTalentSoftSkills)),
    switchMap(([ action, skills ]) => {
      if (!skills) {
        return this.apiService.getUserSoftSkills().pipe(
          map(skillsList => OnboardingActions.GetSoftSkillsSuccess({ skillsList })),
          catchError((err) => of(OnboardingActions.GetSoftSkillsFailure({ err })))
        )
      }
      return of();
    })
  ));

  setSoftSkills$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.SetSoftSkills),
    debounceTime(2000),
    tap((action) => {
      this.apiService.setUserSoftSkills(action.skillsList).subscribe(() => {
        this.store.dispatch(OnboardingActions.SetSoftSkillsSuccess());
      });
    })
  ), { dispatch: false });

  setTalentSeniority$ = createEffect(() => this.actions$.pipe(
    ofType(OnboardingActions.SetTalentSeniority),
    withLatestFrom(this.store.select(selectTalentGeneral)),
    switchMap(([ action, state ]) => {
      if (action.level) {
        const payload = TalentGeneralAdapter.toServer({ ...state, jobTitleLevel: action.level });
        return this.apiService.updateUserGeneralInfo(USER_ROLES.talent, payload).pipe(
          map(() => {
            this.store.dispatch(OnboardingActions.GetSkillsList());
            return OnboardingActions.SetTalentSenioritySuccess({ level: action.level });
          }),
          catchError((err) => of())
        );
      } else {
        return this.apiService.resetTalentHardSkillsPreset().pipe(
          map(() => {
            this.store.dispatch(OnboardingActions.GetSkillsList());
            return OnboardingActions.SetTalentSeniorityFailure();
          }),
          catchError((err) => of())
        );
      }
    })
  ));  

  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private router: Router,
    private store: Store<OnboardingState>,
    private userProxy: UserProxy 
  ) { }

  private setStepsByRole(role): StepItems[] {
    if (role === USER_ROLES.talent) {
      return [
        { key: 'type', path: 'account-type', label: 'Profile Type', isComplited: true },
        { key: 'general', path: 'talent/general', label: 'General information', isComplited: false },
        { key: 'hardSkills', path: 'talent/hard-skills', label: 'Hard skills', isComplited: false },
        // { key: 'softSkills', path: 'talent/soft-skills', label: 'Soft skills', isComplited: false },
        // { key: 'checkout', path: 'talent/checkout', label: 'Review and complete', isComplited: false }
      ];
    }
    if (role === USER_ROLES.hm) {
      return [
        { key: 'type', path: 'account-type', label: 'Profile Type', isComplited: true },
        { key: 'general', path: 'hm/general', label: 'General information', isComplited: false }
      ];
    }
    if (role === USER_ROLES.partner) {
      return [
        { key: 'type', path: 'account-type', label: 'Profile Type', isComplited: true },
        { key: 'general', path: 'partner/general', label: 'General information', isComplited: false }
      ];
    }
    return [];
  }

}
